import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadAttachment, uploadDocument } from '../../api/endpoints';
import { getValidationFailures } from '../../api/http';
import { queryKeys } from '../../api/queryKeys';
import { ValidationFindingsDialog, type ValidationFindings } from '../../components/ValidationFindingsDialog';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';

type UploadMode = 'document' | 'attachment';
type DocumentKind = 'BisDocument' | 'IsdocDocument';

export interface UploadFileDialogProps {
  onClose: () => void;
}

export function UploadFileDialog({ onClose }: UploadFileDialogProps) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [mode, setMode] = useState<UploadMode>('document');
  const [kind, setKind] = useState<DocumentKind>('BisDocument');
  const [file, setFile] = useState<File | null>(null);
  // One key per dialog opening, not per attempt: a retry after a timeout must be recognised as the
  // same upload and de-duplicated rather than stored twice.
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => crypto.randomUUID());

  // What the checks said about the document: what refused it, or what was worth mentioning about one
  // that got in anyway. Shown in a dialog of its own — the findings table is unreadable at this
  // form's width.
  const [findings, setFindings] = useState<ValidationFindings | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error(t('files.upload.chooseFirst'));

      return mode === 'document'
        ? uploadDocument({ file, kind, idempotencyKey })
        : uploadAttachment({ file, idempotencyKey });
    },
    onSuccess: async (result) => {
      const stored = 'file' in result ? result.file : result;
      const found = 'warnings' in result ? result.warnings : [];

      await queryClient.invalidateQueries({ queryKey: queryKeys.files.all });
      toastSuccess(
        stored.deduplicated
          ? t('files.upload.deduplicated', { name: stored.fileName })
          : t('files.upload.uploaded', { name: stored.fileName }),
      );

      // The document is in either way; closing on top of the findings would be the only chance to
      // read them, gone.
      if (found.length > 0)
        setFindings({ severity: 'warning', message: t('files.upload.storedWithWarnings'), items: found });
      else onClose();
    },
    onError: (error) => {
      const found = getValidationFailures(error);
      if (found.length > 0)
        setFindings({ severity: 'error', message: t('files.upload.rejected'), items: found });
      else toastError(error);
    },
  });

  return (
    <>
      <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('files.upload.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl>
              <FormLabel>{t('files.upload.whatIsIt')}</FormLabel>
              <RadioGroup row value={mode} onChange={(event) => setMode(event.target.value as UploadMode)}>
                <FormControlLabel
                  value="document"
                  control={<Radio />}
                  label={t('files.upload.invoiceDocument')}
                />
                <FormControlLabel
                  value="attachment"
                  control={<Radio />}
                  label={t('files.upload.attachment')}
                />
              </RadioGroup>
            </FormControl>

            {mode === 'document' ? (
              <FormControl>
                <FormLabel>{t('files.upload.format')}</FormLabel>
                <RadioGroup
                  row
                  value={kind}
                  onChange={(event) => setKind(event.target.value as DocumentKind)}
                >
                  <FormControlLabel
                    value="BisDocument"
                    control={<Radio />}
                    label={t('fileKinds.BisDocument')}
                  />
                  <FormControlLabel
                    value="IsdocDocument"
                    control={<Radio />}
                    label={t('fileKinds.IsdocDocument')}
                  />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  {t('files.upload.formatNote')}
                </Typography>
              </FormControl>
            ) : (
              <Alert severity="info">{t('files.upload.attachmentNote')}</Alert>
            )}

            <Button variant="outlined" component="label">
              {file ? t('files.upload.selected', { name: file.name }) : t('files.upload.chooseFile')}
              <input
                type="file"
                hidden
                accept={mode === 'document' ? '.xml,text/xml,application/xml' : undefined}
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </Button>

            <TextField
              label={t('files.upload.idempotencyKey')}
              value={idempotencyKey}
              onChange={(event) => setIdempotencyKey(event.target.value)}
              helperText={t('files.upload.idempotencyHelp')}
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={mutation.isPending}>
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => mutation.mutate()}
            disabled={!file || mutation.isPending}
          >
            {t('actions.upload')}
          </Button>
        </DialogActions>
      </Dialog>

      {findings && (
        <ValidationFindingsDialog
          findings={findings}
          onClose={() => {
            setFindings(null);
            // A rejection leaves the form open to fix the file and try again; a warning means the
            // document is already in, so dismissing the report dismisses the upload too.
            if (findings.severity === 'warning') onClose();
          }}
        />
      )}
    </>
  );
}
